import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { calculateBusinessDays, calculateFractionalDays } from '@/lib/utils';
import { JobCategory } from '@/lib/types/job';
import { SystemMilestoneType } from '@/lib/types/milestone';
import { startOfWeek, startOfMonth, format, differenceInDays } from 'date-fns';

// Workflow stages for cycle time calculation
// Each stage includes a description mapping to user-facing workflow steps
export const WORKFLOW_STAGES = [
  { 
    key: 'prep_publishing', 
    label: 'Setup & RFQ Publishing', 
    description: 'Job Creation → Specifications → Delivery Details → RFQ Draft → RFQ Published',
    from: 'job_created' as SystemMilestoneType, 
    to: 'rfq_published' as SystemMilestoneType,
    color: 'hsl(210, 60%, 75%)' 
  },
  { 
    key: 'bidding_award', 
    label: 'Vendor Selection', 
    description: 'RFQ open for bidding → Bids received → Vendor awarded',
    from: 'rfq_published' as SystemMilestoneType, 
    to: 'vendor_awarded' as SystemMilestoneType,
    color: 'hsl(210, 70%, 55%)' 
  },
  { 
    key: 'milestone_planning', 
    label: 'Milestone Planning', 
    description: 'Vendor proposes milestones → Client reviews and approves',
    from: 'vendor_awarded' as SystemMilestoneType, 
    to: 'milestones_approved' as SystemMilestoneType,
    color: 'hsl(270, 60%, 60%)' 
  },
  { 
    key: 'po_processing', 
    label: 'Purchase Order Setup', 
    description: 'PO created → Payment processed → PO sent to vendor',
    from: 'milestones_approved' as SystemMilestoneType, 
    to: 'purchase_order_created' as SystemMilestoneType,
    color: 'hsl(30, 80%, 55%)' 
  },
  { 
    key: 'production_kickoff', 
    label: 'Production Kickoff', 
    description: 'Vendor accepts PO → Work officially begins',
    from: 'purchase_order_created' as SystemMilestoneType, 
    to: 'in_progress' as SystemMilestoneType,
    color: 'hsl(50, 80%, 55%)' 
  },
  { 
    key: 'production_delivery', 
    label: 'Production & Delivery', 
    description: 'Active production → Milestones completed → Delivery proof submitted',
    from: 'in_progress' as SystemMilestoneType, 
    to: 'job_completion_submitted' as SystemMilestoneType,
    color: 'hsl(140, 60%, 50%)' 
  },
  { 
    key: 'client_review', 
    label: 'Final Review & Acceptance', 
    description: 'Client reviews deliverables → Approves or requests changes → Job accepted',
    from: 'job_completion_submitted' as SystemMilestoneType, 
    to: 'job_accepted' as SystemMilestoneType,
    color: 'hsl(175, 60%, 50%)' 
  },
] as const;

export type WorkflowStageKey = typeof WORKFLOW_STAGES[number]['key'];

export interface JobCycleData {
  jobId: string;
  jobPublicId: string;
  jobTitle: string;
  category: JobCategory;
  requesterId: string;
  requesterName: string;
  vendorId: string | null;
  vendorName: string | null;
  createdAt: Date;
  acceptedAt: Date;
  totalCycleTime: number;
  stageDurations: Record<WorkflowStageKey, number>;
}

export interface CycleTimeKPIs {
  avgTotalCycleTime: number;
  medianTotalCycleTime: number;
  avgTimeToAward: number;
  avgProductionTime: number;
  medianProductionTime: number;
  jobsCompleted: number;
  avgJobsPerWeek: number;
  trendVsPreviousPeriod: {
    totalCycleTime: number;
    timeToAward: number;
    productionTime: number;
    jobsCompleted: number;
  };
}

export interface StageBreakdown {
  key: WorkflowStageKey;
  label: string;
  description: string;
  color: string;
  avgDays: number;
  medianDays: number;
  percentOfTotal: number;
}

export interface CategoryCycleTime {
  category: JobCategory;
  avgDays: number;
  medianDays: number;
  jobCount: number;
}

export interface CycleTimeTrendPoint {
  period: string;
  periodStart: Date;
  avgDays: number;
  medianDays: number;
  jobCount: number;
}

export interface RequesterCycleTime {
  userId: string;
  name: string;
  jobCount: number;
  avgTotalCycleTime: number;
  avgTimeToPublish: number;
  avgTimeToAward: number;
  avgProductionTime: number;
}

export interface VendorProductionTime {
  vendorId: string;
  vendorName: string;
  jobCount: number;
  avgProductionTime: number;
  medianProductionTime: number;
  fastestJob: number;
  slowestJob: number;
}

export interface CycleTimeDistributionBucket {
  bucket: string;
  min: number;
  max: number;
  count: number;
  percentage: number;
}

// Helper functions
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

// Main hook to fetch all cycle time data
export function useCycleTimeData(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[] = []
) {
  return useQuery({
    queryKey: ['cycle-time-data', startDate.toISOString(), endDate.toISOString(), organizationId, categories],
    queryFn: async (): Promise<JobCycleData[]> => {
      if (!organizationId) return [];

      // Fetch jobs that were accepted within the date range
      let jobsQuery = supabase
        .from('jobs')
        .select(`
          id,
          public_id,
          title,
          category,
          created_by_user_id,
          created_at,
          accepted_at,
          organization_id
        `)
        .eq('organization_id', organizationId)
        .in('status', ['accepted', 'closed'])
        .gte('accepted_at', startDate.toISOString())
        .lte('accepted_at', endDate.toISOString());

      if (categories.length > 0) {
        jobsQuery = jobsQuery.in('category', categories);
      }

      const { data: jobs, error: jobsError } = await jobsQuery;
      if (jobsError) throw jobsError;
      if (!jobs || jobs.length === 0) return [];

      const jobIds = jobs.map(j => j.id);

      // Fetch milestones for these jobs
      const { data: milestones, error: milestonesError } = await supabase
        .from('milestones')
        .select('job_id, system_milestone_type, completed_at')
        .in('job_id', jobIds)
        .not('system_milestone_type', 'is', null)
        .not('completed_at', 'is', null)
        .order('milestone_order', { ascending: true });

      if (milestonesError) throw milestonesError;

      // Fetch requester profiles from client_profiles
      const requesterIds = [...new Set(jobs.map(j => j.created_by_user_id))];
      const { data: clientProfiles } = await supabase
        .from('client_profiles')
        .select('user_id, first_name, last_name')
        .in('user_id', requesterIds);

      const profileMap = new Map(clientProfiles?.map(p => [p.user_id, `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown']) || []);

      // Fetch awards and vendor info
      const { data: awards } = await supabase
        .from('awards')
        .select('job_id, winning_bid_id')
        .in('job_id', jobIds);

      const bidIds = awards?.map(a => a.winning_bid_id).filter(Boolean) || [];
      const { data: bids } = await supabase
        .from('bids')
        .select('id, vendor_user_id')
        .in('id', bidIds);

      const vendorUserIds = bids?.map(b => b.vendor_user_id) || [];
      const { data: vendorProfiles } = await supabase
        .from('vendor_profiles_enhanced')
        .select('user_id, business_name')
        .in('user_id', vendorUserIds);

      // Build lookup maps
      const bidVendorMap = new Map(bids?.map(b => [b.id, b.vendor_user_id]) || []);
      const vendorNameMap = new Map(vendorProfiles?.map(v => [v.user_id, v.business_name]) || []);
      const jobAwardMap = new Map(awards?.map(a => [a.job_id, a.winning_bid_id]) || []);

      // Group milestones by job
      const milestonesByJob = new Map<string, Map<SystemMilestoneType, Date>>();
      milestones?.forEach(m => {
        if (!milestonesByJob.has(m.job_id)) {
          milestonesByJob.set(m.job_id, new Map());
        }
        milestonesByJob.get(m.job_id)!.set(
          m.system_milestone_type as SystemMilestoneType,
          new Date(m.completed_at!)
        );
      });

      // Calculate cycle times for each job
      const jobCycleData: JobCycleData[] = [];

      for (const job of jobs) {
        const jobMilestones = milestonesByJob.get(job.id);
        if (!jobMilestones) continue;

        const jobCreated = jobMilestones.get('job_created');
        const jobAccepted = jobMilestones.get('job_accepted');

        if (!jobCreated || !jobAccepted) continue;

        const totalCycleTime = calculateBusinessDays(jobCreated, jobAccepted);

        // Calculate stage durations
        const stageDurations: Record<WorkflowStageKey, number> = {} as Record<WorkflowStageKey, number>;

        for (const stage of WORKFLOW_STAGES) {
          const fromDate = jobMilestones.get(stage.from);
          const toDate = jobMilestones.get(stage.to);

          if (fromDate && toDate) {
            stageDurations[stage.key] = calculateFractionalDays(fromDate, toDate);
          } else {
            stageDurations[stage.key] = 0;
          }
        }

        // Get vendor info
        const winningBidId = jobAwardMap.get(job.id);
        const vendorUserId = winningBidId ? bidVendorMap.get(winningBidId) : null;
        const vendorName = vendorUserId ? vendorNameMap.get(vendorUserId) : null;

        jobCycleData.push({
          jobId: job.id,
          jobPublicId: job.public_id,
          jobTitle: job.title,
          category: job.category as JobCategory,
          requesterId: job.created_by_user_id,
          requesterName: profileMap.get(job.created_by_user_id) || 'Unknown',
          vendorId: vendorUserId || null,
          vendorName: vendorName || null,
          createdAt: new Date(job.created_at),
          acceptedAt: new Date(job.accepted_at!),
          totalCycleTime,
          stageDurations,
        });
      }

      return jobCycleData;
    },
    enabled: !!organizationId,
  });
}

// Hook for previous period comparison
export function usePreviousPeriodData(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[] = []
) {
  const periodLength = differenceInDays(endDate, startDate);
  const prevEndDate = new Date(startDate);
  prevEndDate.setDate(prevEndDate.getDate() - 1);
  const prevStartDate = new Date(prevEndDate);
  prevStartDate.setDate(prevStartDate.getDate() - periodLength);

  return useCycleTimeData(prevStartDate, prevEndDate, organizationId, categories);
}

// Compute KPIs from cycle time data
export function computeKPIs(
  currentData: JobCycleData[],
  previousData: JobCycleData[],
  startDate: Date,
  endDate: Date
): CycleTimeKPIs {
  const totalCycleTimes = currentData.map(j => j.totalCycleTime);
  const timesToAward = currentData.map(j => 
    j.stageDurations.prep_publishing + j.stageDurations.bidding_award
  );
  const productionTimes = currentData.map(j => j.stageDurations.production_delivery);

  const prevTotalCycleTimes = previousData.map(j => j.totalCycleTime);
  const prevTimesToAward = previousData.map(j => 
    j.stageDurations.prep_publishing + j.stageDurations.bidding_award
  );
  const prevProductionTimes = previousData.map(j => j.stageDurations.production_delivery);

  const weeks = Math.max(1, differenceInDays(endDate, startDate) / 7);

  return {
    avgTotalCycleTime: average(totalCycleTimes),
    medianTotalCycleTime: median(totalCycleTimes),
    avgTimeToAward: average(timesToAward),
    avgProductionTime: average(productionTimes),
    medianProductionTime: median(productionTimes),
    jobsCompleted: currentData.length,
    avgJobsPerWeek: currentData.length / weeks,
    trendVsPreviousPeriod: {
      totalCycleTime: previousData.length > 0 
        ? ((average(totalCycleTimes) - average(prevTotalCycleTimes)) / average(prevTotalCycleTimes)) * 100 
        : 0,
      timeToAward: previousData.length > 0 
        ? ((average(timesToAward) - average(prevTimesToAward)) / average(prevTimesToAward)) * 100 
        : 0,
      productionTime: previousData.length > 0 
        ? ((average(productionTimes) - average(prevProductionTimes)) / average(prevProductionTimes)) * 100 
        : 0,
      jobsCompleted: previousData.length > 0 
        ? ((currentData.length - previousData.length) / previousData.length) * 100 
        : 0,
    },
  };
}

// Compute workflow stage breakdown
export function computeStageBreakdown(data: JobCycleData[]): StageBreakdown[] {
  if (data.length === 0) return [];

  // First calculate all stage averages using consistent units (fractional days)
  const stageAvgs = WORKFLOW_STAGES.map(stage => {
    const stageTimes = data.map(j => j.stageDurations[stage.key]);
    return average(stageTimes);
  });

  // Sum of all stage averages - this ensures percentages sum to 100%
  const totalStageAvg = stageAvgs.reduce((sum, avg) => sum + avg, 0);

  return WORKFLOW_STAGES.map((stage, index) => {
    const stageTimes = data.map(j => j.stageDurations[stage.key]);
    const avgDays = stageAvgs[index];

    return {
      key: stage.key,
      label: stage.label,
      description: stage.description,
      color: stage.color,
      avgDays,
      medianDays: median(stageTimes),
      percentOfTotal: totalStageAvg > 0 ? (avgDays / totalStageAvg) * 100 : 0,
    };
  });
}

// Compute cycle time by category
export function computeCategoryBreakdown(data: JobCycleData[]): CategoryCycleTime[] {
  const categoryMap = new Map<JobCategory, number[]>();

  data.forEach(job => {
    if (!categoryMap.has(job.category)) {
      categoryMap.set(job.category, []);
    }
    categoryMap.get(job.category)!.push(job.totalCycleTime);
  });

  return Array.from(categoryMap.entries())
    .map(([category, times]) => ({
      category,
      avgDays: average(times),
      medianDays: median(times),
      jobCount: times.length,
    }))
    .sort((a, b) => b.avgDays - a.avgDays);
}

// Compute trend over time
export function computeTrend(
  data: JobCycleData[],
  startDate: Date,
  endDate: Date
): CycleTimeTrendPoint[] {
  const periodDays = differenceInDays(endDate, startDate);
  const useWeekly = periodDays < 90;

  const periodMap = new Map<string, JobCycleData[]>();

  data.forEach(job => {
    const periodStart = useWeekly 
      ? startOfWeek(job.acceptedAt, { weekStartsOn: 1 })
      : startOfMonth(job.acceptedAt);
    const key = periodStart.toISOString();

    if (!periodMap.has(key)) {
      periodMap.set(key, []);
    }
    periodMap.get(key)!.push(job);
  });

  return Array.from(periodMap.entries())
    .map(([key, jobs]) => {
      const periodStart = new Date(key);
      const times = jobs.map(j => j.totalCycleTime);

      return {
        period: useWeekly 
          ? format(periodStart, 'MMM d')
          : format(periodStart, 'MMM yyyy'),
        periodStart,
        avgDays: average(times),
        medianDays: median(times),
        jobCount: jobs.length,
      };
    })
    .sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
}

// Compute requester breakdown
export function computeRequesterBreakdown(data: JobCycleData[]): RequesterCycleTime[] {
  const requesterMap = new Map<string, { name: string; jobs: JobCycleData[] }>();

  data.forEach(job => {
    if (!requesterMap.has(job.requesterId)) {
      requesterMap.set(job.requesterId, { name: job.requesterName, jobs: [] });
    }
    requesterMap.get(job.requesterId)!.jobs.push(job);
  });

  return Array.from(requesterMap.entries())
    .map(([userId, { name, jobs }]) => ({
      userId,
      name,
      jobCount: jobs.length,
      avgTotalCycleTime: average(jobs.map(j => j.totalCycleTime)),
      avgTimeToPublish: average(jobs.map(j => j.stageDurations.prep_publishing)),
      avgTimeToAward: average(jobs.map(j => j.stageDurations.bidding_award)),
      avgProductionTime: average(jobs.map(j => j.stageDurations.production_delivery)),
    }))
    .sort((a, b) => b.jobCount - a.jobCount);
}

// Compute vendor production times
export function computeVendorBreakdown(data: JobCycleData[]): VendorProductionTime[] {
  const vendorMap = new Map<string, { name: string; productionTimes: number[] }>();

  data.forEach(job => {
    if (!job.vendorId || !job.vendorName) return;
    
    if (!vendorMap.has(job.vendorId)) {
      vendorMap.set(job.vendorId, { name: job.vendorName, productionTimes: [] });
    }
    vendorMap.get(job.vendorId)!.productionTimes.push(job.stageDurations.production_delivery);
  });

  return Array.from(vendorMap.entries())
    .filter(([_, { productionTimes }]) => productionTimes.length >= 2) // Min 2 jobs
    .map(([vendorId, { name, productionTimes }]) => ({
      vendorId,
      vendorName: name,
      jobCount: productionTimes.length,
      avgProductionTime: average(productionTimes),
      medianProductionTime: median(productionTimes),
      fastestJob: Math.min(...productionTimes),
      slowestJob: Math.max(...productionTimes),
    }))
    .sort((a, b) => b.jobCount - a.jobCount);
}

// Compute distribution buckets
export function computeDistribution(data: JobCycleData[]): {
  buckets: CycleTimeDistributionBucket[];
  mean: number;
  median: number;
  percentUnder15Days: number;
} {
  const bucketDefs = [
    { bucket: '0-5 days', min: 0, max: 5 },
    { bucket: '5-10 days', min: 5, max: 10 },
    { bucket: '10-15 days', min: 10, max: 15 },
    { bucket: '15-20 days', min: 15, max: 20 },
    { bucket: '20-30 days', min: 20, max: 30 },
    { bucket: '30+ days', min: 30, max: Infinity },
  ];

  const cycleTimes = data.map(j => j.totalCycleTime);
  const total = cycleTimes.length;

  const buckets = bucketDefs.map(def => {
    const count = cycleTimes.filter(t => t >= def.min && t < def.max).length;
    return {
      ...def,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    };
  });

  const under15 = cycleTimes.filter(t => t < 15).length;

  return {
    buckets,
    mean: average(cycleTimes),
    median: median(cycleTimes),
    percentUnder15Days: total > 0 ? (under15 / total) * 100 : 0,
  };
}
